import { action, computed, observable } from 'mobx';
import TransactionRepository from '../../repositories/TransactionRepository';
import CharityDonationRepository from '../../repositories/CharityDonationRepository/CharityDonationRepository';
import { ICharityDonation, IDonationSku } from '../../api/dataMapper/donationApiMapper';
import STATUS from '../../constants/Status';
import CartItem from '../../domain/entities/Cart/CartItem';
import PaymentService from '../../application/services/PaymentService';
import PAYMENT_PROVIDERS from '../../constants/PaymentProviders';

export const noneDonationOption: IDonationSku = {
  amount: null,
  skuId: 0,
};

interface ICharityDonationControllerConfig {
  charityDonationRepository: CharityDonationRepository;
  transactionRepository: TransactionRepository;
  paymentService: PaymentService;
}

interface ICharityDonationData {
  charityLoadedStatus: STATUS;
  donationUpdateStatus: STATUS;
  charityDonations: ICharityDonation[];
  selectedCharityId: number;
  selectedDonationId: number;
  selectedCharity: ICharityDonation;
  donations: CartItem[];
  shouldUpdateDonation: boolean;
  isOpenMode: boolean;
  isPaymentMethodSupported: boolean;
}

export default class CharityDonationViewController {

  private charityDonationRepository: CharityDonationRepository;

  private transactionRepository: TransactionRepository;
 
  private paymentService: PaymentService;

  @observable private selectedDonationId: number;

  @observable private selectedCharityId: number;

  @observable private isOpenMode: boolean;

  constructor(config: ICharityDonationControllerConfig) {
    this.charityDonationRepository = config.charityDonationRepository;
    this.transactionRepository = config.transactionRepository;
    this.paymentService = config.paymentService;
  }

  @action public async load(): Promise<void> {
    await this.charityDonationRepository.loadDonations();

    if (this.cartDonations.length > 0) {
      const firstDonation = this.cartDonations[0];
      this.onSelectCharity(firstDonation.productBaseId);
    }

    if (!this.selectedCharityId) {
      this.onSelectCharity(this.charityDonationRepository.charityDonations[0]?.id);
    }
  }

  @action
  public setOpenMode = (isOpen: boolean): void => {
    this.isOpenMode = isOpen;
  };

  public onAddDonation = async (skuId: number): Promise<void> => {
    if (skuId !== noneDonationOption.skuId) {
      await this.charityDonationRepository.addDonation(skuId);
    } else {
      // user selected no donation, and is trying to remove from current charity
      const skuIdToRemove = this.getCartItemForSelectedCharity.skuBaseId;
      await this.charityDonationRepository.removeDonations([skuIdToRemove]);
    }
    this.setDonation(skuId);
    this.setOpenMode(false);
  };

  public removeCartDonations = async (): Promise<void> => {
    const skusIds = this.cartDonations.map(item => item.skuBaseId);
    await this.charityDonationRepository.removeDonations(skusIds);
    this.setDonation(noneDonationOption.skuId);
    this.clearCharitySelection();
  };

  @action
  private clearCharitySelection(): void {
    this.setDonation(noneDonationOption.skuId);
    this.selectedCharityId = 0;
  }

  @action
  public onSelectCharity = (id: number): void => {
    const donationInCart = this.cartDonations.find(item => item.productBaseId === id);
    if (donationInCart) {
      this.selectedDonationId = donationInCart.skuBaseId;
    }
    this.selectedCharityId = id;
  };

  @action
  private setDonation = (id: number): void => {
    this.selectedDonationId = id;
  };

  @computed
  private get selectedCharity(): ICharityDonation {
    const { charityDonations } = this.charityDonationRepository;
  
    return charityDonations.find(item => item.id === this.selectedCharityId);
  }

  @computed
  private get cartDonations(): CartItem[] {
    const { transaction } = this.transactionRepository;
  
    return transaction?.donations || [];
  }

  @computed
  private get getCartItemForSelectedCharity(): CartItem {
    return this.cartDonations.find(item => item.productBaseId === this.selectedCharityId);
  }

  @computed
  private get isDonationSupportedForSelectedPayment(): boolean {
    if (this.paymentService?.paymentProvider === PAYMENT_PROVIDERS.AFTERPAY) {
      return false;
    }

    return true;
  }

  @computed
  get data(): ICharityDonationData {
    const {
      donationUpdateStatus,
      charityLoadedStatus,
      charityDonations,
    } = this.charityDonationRepository;

    return {
      charityDonations,
      charityLoadedStatus,
      donationUpdateStatus,
      donations: this.cartDonations,
      isOpenMode: this.isOpenMode,
      isPaymentMethodSupported: this.isDonationSupportedForSelectedPayment,
      selectedCharity: this.selectedCharity,
      selectedCharityId: this.selectedCharityId,
      selectedDonationId: this.selectedDonationId,
      shouldUpdateDonation: !!this.getCartItemForSelectedCharity,
    };
  }
}
